/** Keys a patch may never introduce. */
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
function isObject(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function entityId(v) {
    if (!isObject(v))
        return null;
    const id = v['id'];
    return typeof id === 'number' ? id : null;
}
function isEntityArray(arr) {
    return arr.every((el) => entityId(el) !== null);
}
/** The replacement delta for a value: bare for the scalar kinds, wrapped otherwise. */
function replacement(next) {
    const t = typeof next;
    return t === 'number' || t === 'string' || t === 'boolean' ? next : { r: next };
}
/** The delta from `base` to `next`, or null when they are equal. */
export function diffValue(base, next) {
    if (base === next)
        return null;
    if (Array.isArray(base) && Array.isArray(next)) {
        if ((base.length > 0 || next.length > 0) && isEntityArray(base) && isEntityArray(next)) {
            return diffEntities(base, next);
        }
        return diffArray(base, next);
    }
    if (isObject(base) && isObject(next))
        return diffObject(base, next);
    return replacement(next);
}
function diffObject(base, next) {
    const o = {};
    let changed = false;
    for (const key of Object.keys(next)) {
        if (next[key] === undefined)
            continue;
        const d = diffValue(base[key], next[key]);
        // A bare 0, '' or false is a real change; only null means "equal".
        if (d !== null) {
            o[key] = d;
            changed = true;
        }
    }
    const removed = Object.keys(base).filter((k) => base[k] !== undefined && !(k in next));
    if (!changed && removed.length === 0)
        return null;
    return removed.length > 0 ? { o, d: removed } : { o };
}
function diffArray(base, next) {
    const a = {};
    let changed = base.length !== next.length;
    for (let i = 0; i < next.length; i++) {
        const d = i < base.length ? diffValue(base[i], next[i]) : replacement(next[i]);
        if (d !== null) {
            a[String(i)] = d;
            changed = true;
        }
    }
    return changed ? { a, n: next.length } : null;
}
function diffEntities(base, next) {
    const byId = new Map();
    for (const el of base) {
        const id = entityId(el);
        if (id !== null)
            byId.set(id, el);
    }
    const e = {};
    const k = [];
    let changed = base.length !== next.length;
    for (let i = 0; i < next.length; i++) {
        const el = next[i];
        const id = entityId(el);
        if (id === null)
            continue;
        k.push(id);
        if (!changed && entityId(base[i]) !== id)
            changed = true;
        if (!byId.has(id)) {
            e[String(id)] = { r: el };
            changed = true;
        }
        else {
            const d = diffValue(byId.get(id), el);
            if (d !== null) {
                e[String(id)] = d;
                changed = true;
            }
        }
    }
    return changed ? { e, k } : null;
}
/** `base` patched by `delta`; neither input is modified. */
export function applyDelta(base, delta) {
    if (typeof delta !== 'object')
        return delta;
    if ('r' in delta)
        return delta.r;
    if ('o' in delta) {
        const src = isObject(base) ? base : {};
        const out = { ...src };
        for (const [key, d] of Object.entries(delta.o)) {
            if (UNSAFE_KEYS.has(key))
                continue;
            out[key] = applyDelta(src[key], d);
        }
        for (const key of delta.d ?? [])
            delete out[key];
        return out;
    }
    if ('a' in delta) {
        const src = Array.isArray(base) ? base : [];
        const out = new Array(delta.n);
        for (let i = 0; i < delta.n; i++) {
            const d = delta.a[String(i)];
            out[i] = d !== undefined ? applyDelta(src[i], d) : src[i];
        }
        return out;
    }
    const src = Array.isArray(base) ? base : [];
    const byId = new Map();
    for (const el of src) {
        const id = entityId(el);
        if (id !== null)
            byId.set(id, el);
    }
    return delta.k.map((id) => {
        const d = delta.e[String(id)];
        const prev = byId.get(id);
        return d !== undefined ? applyDelta(prev, d) : prev;
    });
}
/** The state without its derived per-tick events (they travel beside the delta). */
function withoutEvents(state) {
    return { ...state, events: [] };
}
/** Delta between two match states, ignoring events; null when nothing but events changed. */
export function diffState(base, next) {
    return diffValue(withoutEvents(base), withoutEvents(next));
}
/** Applies a state delta and attaches the events that came with it. */
export function applyStateDelta(base, delta, events = []) {
    const patched = delta !== null ? applyDelta(withoutEvents(base), delta) : { ...base };
    patched.events = events;
    return patched;
}
//# sourceMappingURL=delta.js.map